/* Formatierung von Datum, Namen und Zeiträumen.
 *
 * Reine Funktionen, keine Texte. Wo ein Ergebnis Text enthalten müsste, wird
 * stattdessen ein Übersetzungsschlüssel samt Parametern zurückgegeben — die
 * Komponente übersetzt. Siehe periodDescriptor().
 */

/**
 * Datum in Tagesdarstellung (TT.MM.JJJJ). Nimmt die drei Schreibweisen an,
 * die im Bestand vorkommen, und lässt alles Unbekannte unverändert durch.
 */
export function fmtDate(d) {
  if (!d) return '';
  let y, m, day;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    [y, m, day] = d.split('-');
  } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) {
    return d;
  } else if (/^\d{2}\.\d{2}\.\d{2}$/.test(d)) {
    [day, m, y] = d.split('.');
    y = '20' + y;
  } else {
    return d;
  }
  return `${day}.${m}.${y}`;
}

/**
 * Zeitstempel als "TT.MM.JJJJ HH:MM" (24 Stunden, Ortszeit des Geräts) — in jeder Sprache und jeder Browser-Einstellung
 * gleich. Früher toLocaleString(Sprache): in en "10/17/2026, 3:04:05 PM". Ungültiges oder leeres → ''.
 * Der Zeitstempel ist ein echter Zeitpunkt (mit Zeitzone, z. B. created_at); er wird in Ortszeit umgerechnet.
 */
export function fmtDateTime(ts) {
  if (ts == null || ts === '') return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Neueste zuerst; Einträge ohne Datum ans Ende. */
export function sortByDate(arr, dateKey) {
  return arr.slice().sort((a, b) => {
    const da = a[dateKey] || a.date || a.signedDate || '';
    const db = b[dateKey] || b.date || b.signedDate || '';
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db.localeCompare(da);
  });
}

/**
 * Zeitraum einer Einladung als Übersetzungsschlüssel + Parameter.
 * Beispiel: { key: 'period.fromUntil', params: { from: '04.07.2026', until: '18.07.2026' } }
 */
export function periodDescriptor(inv) {
  if (inv.periodMode === 'tba') return { key: 'period.tba' };
  if (inv.periodMode === 'open') {
    return inv.from
      ? { key: 'period.fromOpen', params: { from: fmtDate(inv.from) } }
      : { key: 'period.openEnd' };
  }
  if (inv.from && inv.until) {
    return {
      key: 'period.fromUntil',
      params: { from: fmtDate(inv.from), until: fmtDate(inv.until) },
    };
  }
  if (inv.from) return { key: 'period.from', params: { from: fmtDate(inv.from) } };
  if (inv.period) return { key: 'period.raw', params: { text: inv.period } };
  return { key: 'period.none' };
}

/** Alter aus dem Geburtsdatum; null wenn kein Datum hinterlegt ist. */
export function ageFromDob(dob, now = Date.now()) {
  if (!dob) return null;
  const b = new Date(dob);
  const today = new Date(now);
  let age = today.getFullYear() - b.getFullYear();
  const beforeBirthday =
    today.getMonth() < b.getMonth() ||
    (today.getMonth() === b.getMonth() && today.getDate() < b.getDate());
  if (beforeBirthday) age--;
  return age;
}

/**
 * Erster Buchstabe groß, Rest unverändert — nur für die Anzeige (z. B. bevorzugter Fuß: "right" → "Right").
 * Der gespeicherte Wert bleibt, wie er ist. Leere Werte bleiben leer.
 */
export function capitalizeFirst(text) {
  const s = typeof text === 'string' ? text : text == null ? '' : String(text);
  return s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : '';
}

export function fullName(player) {
  return `${player.firstName || ''} ${player.lastName || ''}`.trim();
}

export function initials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .map((x) => x[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Betrag in Euro mit Tausenderpunkten: 65000 → "65.000 €"; leer → "". */
export function fmtEuro(n) {
  if (n === '' || n == null || !Number.isFinite(Number(n))) return '';
  return `${Number(n).toLocaleString('de-DE', { maximumFractionDigits: 2 })} €`;
}

/** Prozentwert: 10 → "10 %"; leer → "". */
export function fmtPercent(n) {
  if (n === '' || n == null || !Number.isFinite(Number(n))) return '';
  return `${Number(n).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}
